import type { IGSetupGuidePriceStatuses, ISetupGuideCampaignPrice } from '../types';
import type { IGSetupGuideItem } from '~/presentation/common';

import { useShopMetaCreateOrUpdateMutation } from '~/api/graphql/mutations/shopMetaCreateOrUpdate.generated';
import {
  mergeSetupGuideStatuses,
  useGSetupGuideContext,
  useWindowSize,
} from '~/presentation/common';
import { useSetupGuideState } from '~/presentation/common/components/GSetupGuide/useSetupGuideState';
import { GEMX_PRICE_SETUP_GUIDE_KEY } from '~/presentation/common/components/GSetupGuide/constants';
import { useShopMetaByKeyQuery } from '~/api/graphql/queries/shopMetaByKey.generated';
import { EBaseSetupGuideCampaign, IVariantType } from '~/modules/campaign/types';
import { usePriceSetupGuideFirstVisit } from './usePriceSetupGuideFirstVisit';
import { findVariantByType, useCampaignContext } from '~/modules/campaign';
import { captureSentryError } from '~/exceptions/sentry';
import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { ESetupGuideCampaignPrice } from '../types';
import { useTranslation } from 'react-i18next';
import { useEffectRunOnce } from '~/hooks';
import { parseShopMeta } from '~/utils';

export interface ICampaignPriceSetupGuide {
  setupGuideState: boolean;
  setupGuideSteps: IGSetupGuideItem<ISetupGuideCampaignPrice>[];
  handleShowSetupGuide: (shouldCheck?: boolean) => void;
  handleUpdateSetupGuide: (setupGuideKeys: ISetupGuideCampaignPrice[]) => void;
}

export const useCampaignPriceSetupGuide = (): ICampaignPriceSetupGuide => {
  const { setupGuideState, setShowSetupGuide } = useSetupGuideState();

  const { t } = useTranslation();
  const { windowWidth } = useWindowSize();
  const { campaign } = useCampaignContext();
  const { expandedId, setExpandedId } = useGSetupGuideContext();

  const queryClient = useQueryClient();

  const { data: setupGuideStatuses, isFetched: isFetchedSetupGuideMetaData } =
    useShopMetaByKeyQuery<IGSetupGuidePriceStatuses>(
      { key: GEMX_PRICE_SETUP_GUIDE_KEY },
      { select: parseShopMeta },
    );
  const shopMetaCreateOrUpdateMutation = useShopMetaCreateOrUpdateMutation();

  usePriceSetupGuideFirstVisit({ isReady: isFetchedSetupGuideMetaData });

  const isCampaignRunning = campaign?.status === CAMPAIGN_STATUS.RUNNING;
  // Both variants exist from the moment the draft is created, so only a picked product counts — and
  // the Variant is duplicated on save, so the Control alone is enough to start.
  const isSelectedControl = !!findVariantByType(campaign, IVariantType.VARIANT_A)?.price?.baseID;

  const setupGuideSteps: IGSetupGuideItem<ISetupGuideCampaignPrice>[] = useMemo(() => {
    const isCompletedTestedProduct =
      setupGuideStatuses?.[ESetupGuideCampaignPrice.CHOOSE_TESTED_PRODUCT] ?? false;

    // The only product action is picking the Control. The duplicate and its price write both happen
    // together on save, so there is no separate "set up the Variant product" step to show.
    return [
      {
        id: ESetupGuideCampaignPrice.CHOOSE_TESTED_PRODUCT,
        title: t('Choose tested product'),
        description: t(
          "This is the active product you're selling. Its current price becomes the Control, the baseline every result is compared against.",
        ),
        completed: isCompletedTestedProduct,
        isHiddenAction: isCompletedTestedProduct && isSelectedControl,
        isMarkDoneByAction: true,
      },
      {
        id: ESetupGuideCampaignPrice.EDIT_VARIANT_PRICE,
        title: t('Edit the Variant price'),
        description: t(
          'Set the price you want to test for the Variant. Every product in the list needs its own amount — saving duplicates the Control for you and writes the new prices to it.',
        ),
        completed: setupGuideStatuses?.[ESetupGuideCampaignPrice.EDIT_VARIANT_PRICE] ?? false,
        disabledReason: !isSelectedControl
          ? t('Please choose the tested product to proceed.')
          : undefined,
        isMarkDoneByAction: true,
      },
      {
        id: EBaseSetupGuideCampaign.SETUP_TRAFFIC_SPLIT,
        title: t('Set traffic split'),
        description: t('Decide what percentage of visitors will see the Control vs. the Variant.'),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.SETUP_TRAFFIC_SPLIT] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.SET_WINNING_METRIC,
        title: t('Set winning metric'),
        description: t(
          'Revenue is the default for price tests, a higher price can win on revenue even when it converts less often.',
        ),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.SET_WINNING_METRIC] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.SET_UP_DEVICE_VISITOR_TRAFFIC_SOURCE_MARKET,
        title: t('Set up device, visitor & traffic source'),
        description: t(
          'Include only visitors from selected devices, visitor types (All, New, Returning), traffic sources, and target markets.',
        ),
        completed:
          setupGuideStatuses?.[
            EBaseSetupGuideCampaign.SET_UP_DEVICE_VISITOR_TRAFFIC_SOURCE_MARKET
          ] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.START_EXPERIMENT,
        title: t('Start experiment'),
        description: t(
          'Launch your A/B test and begin collecting data, you can pause or edit at any time.',
        ),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.START_EXPERIMENT] ?? false,
        isHiddenAction: isCampaignRunning,
        disabledReason: !isSelectedControl
          ? t('Please set up the Control & Variant products to proceed.')
          : undefined,
      },
    ];
  }, [t, setupGuideStatuses, isCampaignRunning, isSelectedControl]);

  const handleUpdateSetupGuide = useCallback(
    async (setupGuideKeys: ISetupGuideCampaignPrice[]) => {
      if (!setupGuideStatuses) return;

      const newSetupGuideStatuses = mergeSetupGuideStatuses(setupGuideStatuses, setupGuideKeys);
      if (!newSetupGuideStatuses || shopMetaCreateOrUpdateMutation.isLoading) return;

      try {
        await shopMetaCreateOrUpdateMutation.mutateAsync({
          input: { key: GEMX_PRICE_SETUP_GUIDE_KEY, value: JSON.stringify(newSetupGuideStatuses) },
        });

        queryClient.setQueryData(['ShopMetaByKey', { key: GEMX_PRICE_SETUP_GUIDE_KEY }], {
          shopMetaByKey: { value: JSON.stringify(newSetupGuideStatuses) },
        });
      } catch (error) {
        captureSentryError(error, {
          source: 'useCampaignPriceSetupGuide.handleUpdateSetupGuide',
          tags: { experiment: 'Experiment' },
        });
      }
    },
    [queryClient, shopMetaCreateOrUpdateMutation, setupGuideStatuses],
  );

  const handleShowSetupGuide = useCallback(
    (shouldCheck = false) => {
      if (!shouldCheck && (!windowWidth['1040Down'] || !expandedId)) return;

      setShowSetupGuide();
      setExpandedId(setupGuideSteps.find((item) => !item.completed)?.id);
    },
    [windowWidth, expandedId, setShowSetupGuide, setExpandedId, setupGuideSteps],
  );

  useEffectRunOnce(() => {
    setExpandedId(setupGuideSteps.find((item) => !item.completed)?.id);
  }, isFetchedSetupGuideMetaData && setupGuideState);

  useEffect(() => {
    if (!isFetchedSetupGuideMetaData || shopMetaCreateOrUpdateMutation.isLoading) return;

    if (isSelectedControl) handleUpdateSetupGuide([ESetupGuideCampaignPrice.CHOOSE_TESTED_PRODUCT]);
  }, [
    handleUpdateSetupGuide,
    isFetchedSetupGuideMetaData,
    isSelectedControl,
    shopMetaCreateOrUpdateMutation.isLoading,
  ]);

  return {
    setupGuideSteps,
    setupGuideState,
    handleUpdateSetupGuide,
    handleShowSetupGuide,
  };
};
